import { useMemo } from 'react';
import classNames from 'classnames';

import { ArrowsImage } from '../arrows-image/arrows-image';
import { ColumnContainerText } from '../columns/column-container/column-container-text/column-container-text';
import {
  ColumnContainerTitle,
  ColumnContainerTitleType,
} from '../columns/column-container/column-container-title/column-container-title';
import { getModels, useChildComponents } from '../../utils/use-child-components';
import { useHasGradientBackground, useHasMidBlueBackground } from '../../utils/use-has-background-class';

import { VerticalTabsContainerProps } from './vertical-tabs-container.config';

const modelKeys = ['tabs'];

const VerticalTabsContainer = ({
  additionalClasses,
  appliedCssClassNames,
  cqPath,
  isInEditor,
  text,
  title,
  ...props
}: VerticalTabsContainerProps) => {
  const hasGradientBackground = useHasGradientBackground(appliedCssClassNames);
  const hasMidBlueBackground = useHasMidBlueBackground(appliedCssClassNames);

  const childComponents = useChildComponents(
    cqPath,
    getModels({ cqPath, isInEditor, ...props }, modelKeys),
    {
      isInEditor,
    },
    {
      tabs: {
        hasGradientBackground,
        hasMidBlueBackground,
      },
    }
  );

  const darkBg = useMemo(() => {
    return hasGradientBackground || hasMidBlueBackground;
  }, [hasGradientBackground, hasMidBlueBackground]);

  const styles = useMemo(
    () => ({
      text: {
        'snowflake-vertical-tabs-container-text': true,
        'text-color-text-04': hasMidBlueBackground,
        'text-color-text-04-ui-bg-03': hasGradientBackground,
      },
    }),
    [hasMidBlueBackground, hasGradientBackground]
  );

  const hideArrows = useMemo(() => !title && !text, [title, text]);

  const getMarginBottom = () => {
    if (!text && !!props.tabs) {
      return ColumnContainerTitleType.NONE;
    }
    if (!text) {
      return ColumnContainerTitleType.LARGE;
    }
    return undefined;
  };

  return (
    <div className={classNames('snowflake-vertical-tabs-container-outer', additionalClasses)}>
      {!hideArrows && <ArrowsImage zIndex={-1} darkBg={darkBg} />}
      <div
        className="snowflake-vertical-tabs-container snowflake-container"
        data-testid="snowflake-vertical-tabs-container"
      >
        {title && (
          <ColumnContainerTitle
            title={title}
            marginBottom={getMarginBottom()}
            hasGradientBackground={hasGradientBackground}
            hasMidBlueBackground={hasMidBlueBackground}
          />
        )}
        {text && <ColumnContainerText className={classNames(styles.text)} textProps={text} />}
        {props.tabs && (
          <div
            className="snowflake-vertical-tabs-container-vertical-tabs"
            data-testid="snowflake-vertical-tabs-container-vertical-tabs"
          >
            {childComponents.tabs}
          </div>
        )}
      </div>
    </div>
  );
};

export default VerticalTabsContainer;
